import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../images/logo.png';
import { clearActiveUser } from "../services/authLock";
import { 
  Bot, 
  BarChart3, 
  FolderOpen, 
  FileText, 
  Settings, 
  Activity,
  Users,
  Menu,
  X,
  Stethoscope,
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/', icon: BarChart3 },
  { name: 'Sessions', href: '/sessions', icon: Users },
  { name: 'Projects', href: '/projects', icon: FolderOpen },
  { name: 'Bids', href: '/bids', icon: FileText },
  { name: 'Analytics', href: '/analytics', icon: Activity },
  { name: 'Logs', href: '/logs', icon: Bot },
  { name: 'Diagnostics', href: '/diagnostics', icon: Stethoscope },
  { name: 'Configuration', href: '/configuration', icon: Settings },
];

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

const handleLogout = async () => {
  await fetch("/logout", { method: "POST" });
  sessionStorage.removeItem("auth");
  navigate("/");
  window.location.reload();
};


  return (
    <div className="min-h-screen bg-slate-950">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-slate-900 border-r border-slate-800">
          <div className="flex h-16 items-center justify-between px-4">
            <img src={logo} className="mt-4 opacity-90" alt="" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-slate-400 hover:text-slate-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-primary-900/40 text-primary-200 border border-primary-800/50'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
      <button
        onClick={handleLogout}
        className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-red-400 hover:bg-red-950/40 hover:text-red-300 w-full text-left"
      >
        <svg
          className="mr-3 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1m0-10V5m0 14h2a2 2 0 002-2v-3m0-4V7a2 2 0 00-2-2h-2"
          />
        </svg>
        Logout
      </button>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-slate-900 border-r border-slate-800">
          <div className="flex h-16 items-center px-4">
            <img src={logo} className="mt-4 opacity-90" alt="" />
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-primary-900/40 text-primary-200 border border-primary-800/50'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
      <button
        onClick={handleLogout}
        className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-red-400 hover:bg-red-950/40 hover:text-red-300 w-full text-left"
      >
        <svg
          className="mr-3 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1m0-10V5m0 14h2a2 2 0 002-2v-3m0-4V7a2 2 0 00-2-2h-2"
          />
        </svg>
        Logout
      </button>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-slate-800 bg-slate-900/90 backdrop-blur-md px-4 shadow-sm shadow-black/20 sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-slate-200 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="text-sm text-slate-400 font-mono tabular-nums">
                {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
